/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @next/next/no-img-element */

'use client';

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IcCloseCircle, IconButton } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SnapCurationListItem } from './type';

interface SortableItemProps {
  id: string;
  item: SnapCurationListItem;
  onUnselect: () => void;
}

const SortableItem = ({ id, item, onUnselect }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const [hasImgError, setHasImgError] = useState(false);
  const { service } = useDeviceInfo();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="inline-flex mr-1">
      <div
        className={clsx(
          'relative',
          service === SERVICE.MUSINSA ? 'w-[48px] h-[56px]' : 'w-[48px] h-[48px]',
        )}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onUnselect();
        }}
      >
        <div
          {...attributes}
          {...listeners}
          className={clsx(
            'drag-handle cursor-grab active:cursor-grabbing absolute bottom-0 left-0 rounded overflow-hidden bg-[#e5e5e5]',
            service === SERVICE.MUSINSA ? 'w-[40px] h-[48px]' : 'w-[40px] h-[40px]',
          )}
        >
          {/* mds Image 사용 시, 하단 여백으로 인한 스크롤 발생 */}
          <img
            src={item.imageUrl}
            alt=""
            className={clsx('w-full h-full pointer-events-none', hasImgError && 'invisible')}
            loading="lazy"
            decoding="async"
            width={40}
            height={service === SERVICE.MUSINSA ? 48 : 40}
            onError={() => setHasImgError(true)}
          />
        </div>
        <IconButton
          size="size20x20"
          className="absolute top-0 right-0 z-[100]"
          style={{ pointerEvents: 'auto' }}
        >
          <IcCloseCircle />
        </IconButton>
      </div>
    </div>
  );
};

interface Props {
  selectedSnaps: SnapCurationListItem[];
  onUnselect: (snapId: string) => void;
  onDragEnd?: (items: SnapCurationListItem[]) => void;
}

export const DraggableSelectedSnapList = ({ selectedSnaps, onUnselect, onDragEnd }: Props) => {
  const [dragItems, setDragItems] = useState<SnapCurationListItem[]>([]);
  const { isTouchDevice } = useDeviceInfo();

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 300,
      tolerance: 5,
    },
  });

  const sensors = useSensors(isTouchDevice ? touchSensor : pointerSensor);

  useEffect(() => {
    if (!selectedSnaps.length) {
      setDragItems([]);
      return;
    }
    setDragItems(selectedSnaps);
  }, [selectedSnaps]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || !dragItems.length) {
        return;
      }

      if (active.id !== over.id) {
        const oldIndex = dragItems.findIndex((item) => item.id === String(active.id));
        const newIndex = dragItems.findIndex((item) => item.id === String(over.id));

        const newItems = arrayMove(dragItems, oldIndex, newIndex);
        setDragItems(newItems);
        onDragEnd?.(newItems);
      }
    },
    [dragItems, onDragEnd],
  );

  const handleUnselect = (snapId: string) => {
    onUnselect(snapId);
  };

  const dragItemIds = useMemo(() => dragItems.map((item) => item.id), [dragItems]);

  if (selectedSnaps.length === 0) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      autoScroll={{ threshold: { x: 0.2, y: 0 } }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={dragItemIds} strategy={horizontalListSortingStrategy}>
        <div className="py-1">
          <div className="flex gap-x-[4px] overflow-x-auto scrollbar-hide px-4">
            {dragItems.map((item) => (
              <SortableItem
                key={item.id}
                id={item.id}
                item={item}
                onUnselect={() => handleUnselect(item.id)}
              />
            ))}
          </div>
        </div>
      </SortableContext>
    </DndContext>
  );
};
